<template>
  <div class="homepage">
    <img src="https://uamax.cn/img/logo.1427cd4a.png" alt="logo" class="logo" />
    <header class="header">
      <h1 class="title">落地页导航</h1>
      <p class="subtitle">按分类浏览，点击页面名称跳转</p>
      <div class="search-wrap">
        <input
          v-model="searchKeyword"
          type="text"
          class="search-input"
          placeholder="搜索页面名称或路径..."
          @input="onSearch"
        />
        <span v-if="searchKeyword" class="search-clear" @click="searchKeyword = ''">×</span>
      </div>
    </header>

    <div class="categories">
      <section
        v-for="cat in categoryList"
        :key="cat.key"
        class="category-section"
        :class="{ 'category-empty': !filteredPagesByCategory[cat.key].length }"
      >
        <h2 class="category-title">
          <img :src="cat.icon" alt="category icon" class="category-icon" />
          {{ cat.label }}
          <span class="category-count">({{ filteredPagesByCategory[cat.key].length }})</span>
        </h2>
        <ul class="page-list" v-if="filteredPagesByCategory[cat.key].length">
          <li v-for="page in filteredPagesByCategory[cat.key]" :key="page.path" class="page-item">
            <router-link
              :to="page.path"
              target="_blank"
              class="page-link"
              :class="{ 'page-link--invalid': page.valid === false }"
              >{{ page.name }}</router-link
            >
          </li>
        </ul>
        <p v-else class="no-result">暂无匹配页面</p>
      </section>
    </div>
  </div>
</template>

<script>
function flattenRoutes(routes, pages) {
  routes.forEach((route) => {
    if (route.children && route.children.length) {
      flattenRoutes(route.children, pages);
    }

    if (
      !route ||
      typeof route.path !== 'string' ||
      !route.path.startsWith('/') ||
      route.path === '/' ||
      route.path === '/Homepage' ||
      route.name === 'Homepage' ||
      (route.meta && route.meta.showInHomepage === false)
    ) {
      return;
    }

    const meta = route.meta || {};

    pages.push({
      name: meta.title || route.name || route.path.replace(/^\//, ''),
      path: route.path,
      category: meta.category,
      valid: meta.valid !== false,
    });
  });

  return pages;
}

function sortPagesByName(pages) {
  return pages.slice().sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
}

function getRoutePages(router) {
  const routes = (router && router.options && router.options.routes) || [];
  return flattenRoutes(routes, []);
}

// 落地页分类：seo(抽奖/礼品卡)、nutrition(营养品/药物)、ecommerce(电商)、homeImprovement(家装)、loan(贷款)
const CATEGORIES = [
  {
    key: 'soi',
    label: 'Soi（抽奖/礼品卡）',
    icon: 'https://uamax.obs.ap-southeast-3.myhuaweicloud.com/f9fc238225424e9fbc80348a2394f920.png',
  },
  {
    key: 'nutrition',
    label: 'Nutrition（营养品/药物）',
    icon: 'https://uamax.obs.ap-southeast-3.myhuaweicloud.com/1c0d8cadb8014ee7ab597e5932351247.png',
  },
  {
    key: 'ecommerce',
    label: 'Ecommerce（电商）',
    icon: 'https://uamax.obs.ap-southeast-3.myhuaweicloud.com/7f4ed4a1adf64fb69411fb3ef1f3d23c.png',
  },
  {
    key: 'homeImprovement',
    label: 'HomeImprovement（家装）',
    icon: 'https://uamax.obs.ap-southeast-3.myhuaweicloud.com/360eb0bae0ca4084979569ec8fe09639.png',
  },
  {
    key: 'financial',
    label: 'Financial（金融/贷款）',
    icon: 'https://uamax.obs.ap-southeast-3.myhuaweicloud.com/cd58b21f8f214c77a9c6acea4c5357ac.png',
  },
];

export default {
  name: 'Homepage',
  data() {
    return {
      searchKeyword: '',
      categoryList: CATEGORIES,
    };
  },
  computed: {
    pages() {
      return getRoutePages(this.$router);
    },
    filteredPages() {
      const kw = (this.searchKeyword || '').trim().toLowerCase();
      if (!kw) return this.pages;
      return this.pages.filter(
        (p) => p.name.toLowerCase().includes(kw) || p.path.toLowerCase().includes(kw)
      );
    },
    filteredPagesByCategory() {
      const map = {};
      this.categoryList.forEach((c) => {
        map[c.key] = sortPagesByName(this.filteredPages.filter((p) => p.category === c.key));
      });
      return map;
    },
  },
  methods: {
    onSearch() {
      // 仅依赖 searchKeyword，computed 会自动更新
    },
  },
};
</script>

<style scoped lang="scss">
.homepage {
  position: relative;
  font-family: 'Open Sans', sans-serif;
  min-height: 100vh;
  background: linear-gradient(160deg, #f0f4f8 0%, #e2e8f0 100%);
  padding: 2rem 1.5rem 3rem;
}

.logo {
  width: 100px;
  height: auto;
}

@media (min-width: 769px) {
  .logo {
    position: absolute;
    top: 2rem;
    left: 4rem;
    z-index: 2;
  }
}

.header {
  max-width: 720px;
  margin: 0 auto 2rem;
  text-align: center;
}

.title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
}

.subtitle {
  color: #64748b;
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
}

.search-wrap {
  position: relative;
  max-width: 400px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.25rem 0.75rem 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.search-clear {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  line-height: 20px;
  text-align: center;
  font-size: 1.2rem;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 50%;
}

.search-clear:hover {
  color: #475569;
  background: #f1f5f9;
}

.categories {
  max-width: 900px;
  margin: 0 auto;
}

.category-section {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
}

.category-section.category-empty {
  opacity: 0.85;
}

.category-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.category-icon {
  width: 25px;
}

.category-count {
  font-weight: 400;
  color: #94a3b8;
  font-size: 0.9rem;
}

.page-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.5rem;
}

.page-item {
  margin: 0;
}

.page-link {
  display: block;
  padding: 0.5rem 0.75rem;
  color: #475569;
  text-decoration: none;
  font-size: 0.9rem;
  border-radius: 8px;
  transition: background 0.15s, color 0.15s;
}

.page-link:hover {
  background: #f1f5f9;
  color: #1e40af;
}

.page-link.page-link--invalid {
  color: #dc2626;
}

.page-link.page-link--invalid:hover {
  color: #b91c1c;
}

.no-result {
  margin: 0;
  color: #94a3b8;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .homepage {
    padding: 1.25rem 1rem 2rem;
  }

  .logo {
    display: block;
    width: 88px;
    margin: 0 auto 0.75rem;
  }

  .title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.88rem;
    margin-bottom: 1.25rem;
  }
}
</style>
